// src/services/openingBalanceService.js
// D-296 (09 Oct 2026) — OPENING BALANCES for a company starting on Zeno.
//
// WHY THIS EXISTS
// A new client arrives with history: vendors / subcontractors they already
// owe (or paid in advance), and money already sitting in cash / bank / UPI.
// Without a starting point the khaata and the Cash / Bank Book are wrong from
// day one. This is the ONLY code allowed to write an opening balance.
//
// HOW IT IS STORED — financial_transactions, type "opening_balance":
//   entry_kind "party"    → party_type, party_id, party_name,
//                           side "due" (we owe them) | "advance" (we paid ahead)
//   entry_kind "mode"     → mode_group cash | bank | upi | cheque,
//                           side "positive" | "negative" (overdraft)
//   entry_kind "reversal" → reverses_id; doc id is ALWAYS "rev_" + that id,
//                           so one entry can be reversed only once (the ledger
//                           allows create only — a second write is denied).
// Every row: amount (> 0), as_of ("YYYY-MM-DD"), notes, created_by(_name).
// No project_id on purpose: old money is not this period's project cost.
//
// WHY A NEW TYPE: every existing total filters by its own type ("income",
// "expense", "vendor", "payment"), so an "opening_balance" row is excluded
// from Income, Expense, Home, Reports and Tally automatically. Only khaata.js
// and cashBook.js read it. Rows are never edited — a mistake is reversed and
// entered again (same immutable-ledger rule as payments, SEC-01 / D-289).

import { db } from "../firebase";
import { collection, doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";

export const OPENING_MODES = ["cash", "bank", "upi", "cheque"];
const PARTY_COLLECTION = { vendor: "vendors", subcontractor: "subcontractors" };

// Create the id when the form opens and reuse it on every Save click, so a
// double click can never record the same opening balance twice.
export const newOpeningId = () => doc(collection(db, "financial_transactions")).id;

const toAmount = (v) => Math.round(Number(v || 0) * 100) / 100;
const isDate   = (s) => /^\d{4}-\d{2}-\d{2}$/.test(String(s || ""));

export async function recordOpeningBalance({
  entry_id,
  company_id,
  entry_kind,          // "party" | "mode"
  party_type = "",     // party: "vendor" | "subcontractor"
  party_id = "",
  side,                // party: "due" | "advance"   mode: "positive" | "negative"
  mode_group = "",     // mode: cash | bank | upi | cheque
  amount,
  as_of,               // "YYYY-MM-DD"
  notes = "",
  created_by,
  created_by_name = "",
}) {
  if (!entry_id)   throw new Error("Entry id missing — please reload the page.");
  if (!company_id) throw new Error("Company not loaded.");
  if (!created_by) throw new Error("You are not signed in.");
  const amt = toAmount(amount);
  if (!(amt > 0))     throw new Error("Amount must be greater than 0.");
  if (!isDate(as_of)) throw new Error("Select the 'as of' date.");

  const ref = doc(db, "financial_transactions", entry_id);
  const existing = await getDoc(ref);
  if (existing.exists()) return { entry_id, already_recorded: true };

  const base = {
    company_id,
    type:            "opening_balance",
    entry_kind,
    amount:          amt,
    as_of,
    notes:           String(notes || "").trim(),
    created_by,
    created_by_name,
    created_at:      serverTimestamp(),
  };

  if (entry_kind === "party") {
    if (!PARTY_COLLECTION[party_type]) throw new Error("Select vendor or subcontractor.");
    if (!party_id) throw new Error("Select the vendor / subcontractor.");
    if (!["due", "advance"].includes(side)) throw new Error("Select 'We owe them' or 'Advance given'.");
    const partySnap = await getDoc(doc(db, PARTY_COLLECTION[party_type], party_id));
    if (!partySnap.exists()) throw new Error("This vendor/subcontractor no longer exists.");
    const party = partySnap.data();
    if (party.company_id !== company_id) throw new Error("Access denied.");
    await setDoc(ref, {
      ...base,
      party_type,
      party_id,
      party_name: party.vendor_name || party.name || "",
      side,
    });
    return { entry_id, already_recorded: false };
  }

  if (entry_kind === "mode") {
    if (!OPENING_MODES.includes(mode_group)) throw new Error("Select Cash, Bank, UPI or Cheque.");
    if (!["positive", "negative"].includes(side)) throw new Error("Select balance or overdraft.");
    await setDoc(ref, { ...base, mode_group, side });
    return { entry_id, already_recorded: false };
  }

  throw new Error("Invalid entry.");
}

// Cancels one opening entry. The original row is never touched; a reversal
// row with id "rev_<original id>" makes both drop out of every balance.
export async function reverseOpeningBalance({
  company_id,
  original_id,
  reason = "",
  created_by,
  created_by_name = "",
}) {
  if (!company_id)  throw new Error("Company not loaded.");
  if (!original_id) throw new Error("Entry missing.");
  if (!created_by)  throw new Error("You are not signed in.");
  if (!String(reason).trim()) throw new Error("Enter a reason for reversing.");

  const origSnap = await getDoc(doc(db, "financial_transactions", original_id));
  if (!origSnap.exists()) throw new Error("Entry not found.");
  const orig = origSnap.data();
  if (orig.company_id !== company_id) throw new Error("Access denied.");
  if (orig.type !== "opening_balance" || !["party", "mode"].includes(orig.entry_kind)) {
    throw new Error("Only an opening balance entry can be reversed here.");
  }

  const revRef = doc(db, "financial_transactions", `rev_${original_id}`);
  const revSnap = await getDoc(revRef);
  if (revSnap.exists()) return { already_reversed: true };

  await setDoc(revRef, {
    company_id,
    type:            "opening_balance",
    entry_kind:      "reversal",
    reverses_id:     original_id,
    amount:          toAmount(orig.amount),
    as_of:           orig.as_of || "",
    notes:           String(reason).trim(),
    created_by,
    created_by_name,
    created_at:      serverTimestamp(),
  });
  return { already_reversed: false };
}
